import { TIER_DISPLAY_NAMES, TIER_MIN_STORAGE, formatStorageBytes } from '@/atoms/subscription.atom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import type { BillingInterval, SubscriptionTier } from '@/graphql/graphql';
import { useOrganization } from '@/hooks/useOrganizations';
import { usePricing, type GraduatedTier } from '@/hooks/usePricing';
import { useSubscription } from '@/hooks/useSubscription';
import dayjs from 'dayjs';
import { Minus, Plus } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { toast } from 'sonner';

const STORAGE_BLOCK_SIZE_BYTES = 100 * 1000 * 1000 * 1000; // 100GB per block
const MAX_STORAGE_BLOCKS = 100; // Maximum 10TB

interface StorageManagerProps {
  onStorageChange?: (blocks: number) => void;
  overrideTier?: SubscriptionTier;
  overrideBlocks?: number;
  overrideBillingInterval?: BillingInterval;
  standalone?: boolean;
}

export const StorageManager = ({ onStorageChange, overrideTier, overrideBlocks, overrideBillingInterval, standalone }: StorageManagerProps) => {
  const { subscription, updateStorage, loading } = useSubscription();
  const { calculateStoragePrice, getPricePerBlock, formatPrice, pricing } = usePricing();
  const organization = useOrganization();
  const [pendingBlocks, setPendingBlocks] = useState<number | null>(null);

  const effectiveTier = subscription.scheduledTier ?? subscription.tier;
  const tier = overrideTier ?? effectiveTier;
  const currentBlocks = overrideBlocks ?? subscription.storageBlocks;
  const effectiveBlocks = subscription.scheduledStorageBlocks ?? currentBlocks;
  const displayBlocks = pendingBlocks ?? (standalone ? currentBlocks : effectiveBlocks);
  const minBlocks = TIER_MIN_STORAGE[tier];
  const storageUsedBytes = organization?.storageUsedBytes || 0;
  const billingInterval: BillingInterval = overrideBillingInterval ?? (subscription.billingInterval || 'MONTHLY');
  const intervalLabel = billingInterval === 'MONTHLY' ? 'mo' : 'yr';

  const minBlocksForUsage = useMemo(() => {
    return Math.ceil(storageUsedBytes / STORAGE_BLOCK_SIZE_BYTES) || 1;
  }, [storageUsedBytes]);

  const effectiveMinBlocks = Math.max(minBlocks, minBlocksForUsage);

  const storagePercentage = useMemo(() => {
    const limitBytes = displayBlocks * STORAGE_BLOCK_SIZE_BYTES;
    return limitBytes > 0 ? Math.min((storageUsedBytes / limitBytes) * 100, 100) : 0;
  }, [storageUsedBytes, displayBlocks]);

  const currentPrice = useMemo(
    () => calculateStoragePrice(tier, standalone ? currentBlocks : effectiveBlocks, billingInterval),
    [calculateStoragePrice, tier, standalone, currentBlocks, effectiveBlocks, billingInterval],
  );

  const newPrice = useMemo(
    () => calculateStoragePrice(tier, displayBlocks, billingInterval),
    [calculateStoragePrice, tier, displayBlocks, billingInterval],
  );

  const pricePerBlock = useMemo(
    () => getPricePerBlock(tier, displayBlocks, billingInterval),
    [getPricePerBlock, tier, displayBlocks, billingInterval],
  );

  const priceDifference = newPrice - currentPrice;

  const handleIncrement = () => {
    if (displayBlocks >= MAX_STORAGE_BLOCKS) return;
    const newBlocks = displayBlocks + 1;
    setPendingBlocks(newBlocks);
    onStorageChange?.(newBlocks);
  };

  const handleDecrement = () => {
    if (displayBlocks <= effectiveMinBlocks) return;
    const newBlocks = displayBlocks - 1;
    setPendingBlocks(newBlocks);
    onStorageChange?.(newBlocks);
  };

  const handleApplyChanges = async () => {
    if (pendingBlocks === null || pendingBlocks === currentBlocks) return;
    try {
      await updateStorage(pendingBlocks);
      const isIncrease = pendingBlocks > currentBlocks;
      toast.success(
        isIncrease
          ? `Storage increased to ${pendingBlocks} blocks. Changes applied immediately.`
          : `Storage decrease to ${pendingBlocks} blocks scheduled for end of billing period.`,
      );
      setPendingBlocks(null);
    } catch (err) {
      toast.error('Failed to update storage. Please try again.');
    }
  };

  const handleBlocksInput = useCallback(
    (value: string) => {
      const num = parseInt(value, 10);
      if (isNaN(num) || num < 1) return;
      const clamped = Math.min(num, MAX_STORAGE_BLOCKS);
      setPendingBlocks(clamped);
      onStorageChange?.(clamped);
    },
    [onStorageChange],
  );

  const handleCancel = () => {
    setPendingBlocks(null);
    onStorageChange?.(compareBlocks);
  };

  const compareBlocks = standalone ? currentBlocks : effectiveBlocks;
  const hasChanges = pendingBlocks !== null && pendingBlocks !== compareBlocks;
  const isIncrease = hasChanges && pendingBlocks! > currentBlocks;

  const graduatedTiers: GraduatedTier[] = useMemo(() => {
    if (!pricing) return [];
    const tierPricing = pricing.tiers.find((t) => t.tier === tier);
    if (!tierPricing) return [];
    return billingInterval === 'MONTHLY' ? tierPricing.storage.monthly : tierPricing.storage.annual;
  }, [pricing, tier, billingInterval]);

  const content = (
    <div className='space-y-5'>
      {/* Usage bar */}
      {!standalone && (
        <div className='space-y-1.5'>
          <div className='flex items-center justify-between text-sm'>
            <span className='text-slate-500 dark:text-stone-400'>Usage</span>
            <span className='text-slate-700 dark:text-stone-300'>
              {formatStorageBytes(storageUsedBytes)} / {formatStorageBytes(displayBlocks * STORAGE_BLOCK_SIZE_BYTES)}
            </span>
          </div>
          <Progress value={storagePercentage} className={`h-1.5 ${storagePercentage > 90 ? 'bg-red-100' : ''}`} />
          {storagePercentage > 90 && (
            <p className='text-xs text-red-500'>Running low on storage. Consider adding more blocks.</p>
          )}
        </div>
      )}

      {/* Block selector */}
      <div className='flex items-center justify-center gap-5 py-2'>
        <Button
          variant='outline'
          size='icon'
          className='h-8 w-8'
          onClick={handleDecrement}
          disabled={displayBlocks <= effectiveMinBlocks || loading}>
          <Minus className='h-3.5 w-3.5' />
        </Button>

        <div className='text-center min-w-[120px]'>
          <input
            type='number'
            min={1}
            max={MAX_STORAGE_BLOCKS}
            value={displayBlocks}
            onChange={(e) => handleBlocksInput(e.target.value)}
            className='w-20 text-center text-3xl font-bold text-slate-900 dark:text-white bg-transparent border-b border-transparent hover:border-slate-300 dark:hover:border-stone-600 focus:border-blue-500 focus:outline-none transition-colors [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none'
          />
          <p className='text-xs text-slate-500 dark:text-stone-400'>
            block{displayBlocks !== 1 ? 's' : ''} ({formatStorageBytes(displayBlocks * STORAGE_BLOCK_SIZE_BYTES)})
          </p>
          <p className='text-sm font-semibold text-slate-700 dark:text-stone-300 mt-1'>
            {formatPrice(newPrice)}/{intervalLabel}
          </p>
          <p className='text-[11px] text-slate-400 dark:text-stone-500'>{formatPrice(pricePerBlock)}/block</p>
        </div>

        <Button variant='outline' size='icon' className='h-8 w-8' onClick={handleIncrement} disabled={loading || displayBlocks >= MAX_STORAGE_BLOCKS}>
          <Plus className='h-3.5 w-3.5' />
        </Button>
      </div>

      {/* Volume pricing */}
      {graduatedTiers.length > 0 && (
        <div className='rounded-md bg-slate-50 dark:bg-stone-900 p-3'>
          <p className='text-xs font-medium text-slate-500 dark:text-stone-400 uppercase tracking-wide mb-2'>
            Volume Pricing
          </p>
          <div className='space-y-1'>
            {graduatedTiers.map((gradTier, index) => {
              const prevUpTo = index > 0 ? (graduatedTiers[index - 1].upTo ?? 0) : 0;
              const rangeStart = prevUpTo + 1;
              const rangeEnd = gradTier.upTo;
              const isCurrentTier = displayBlocks >= rangeStart && (rangeEnd == null || displayBlocks <= rangeEnd);

              return (
                <div
                  key={index}
                  className={`flex justify-between text-xs py-1 px-2 rounded ${
                    isCurrentTier ? 'bg-blue-100 dark:bg-blue-900/30 font-medium' : ''
                  }`}>
                  <span className='text-slate-600 dark:text-stone-400'>
                    {rangeEnd == null ? `${rangeStart}+` : `${rangeStart}-${rangeEnd}`} blocks
                  </span>
                  <span className='text-slate-900 dark:text-white'>{gradTier.unitAmountDecimal}/block</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Validation */}
      {displayBlocks < minBlocks && (
        <p className='text-xs text-red-500 text-center'>
          Minimum for {TIER_DISPLAY_NAMES[tier]}: {minBlocks} block{minBlocks !== 1 ? 's' : ''}.
        </p>
      )}
      {displayBlocks < minBlocksForUsage && (
        <p className='text-xs text-red-500 text-center'>
          Cannot go below current usage ({formatStorageBytes(storageUsedBytes)}).
        </p>
      )}
      {displayBlocks >= MAX_STORAGE_BLOCKS && (
        <p className='text-xs text-amber-500 text-center'>
          Maximum {MAX_STORAGE_BLOCKS} blocks ({formatStorageBytes(MAX_STORAGE_BLOCKS * STORAGE_BLOCK_SIZE_BYTES)}). Contact support for more.
        </p>
      )}

      {/* Pending changes (only in managed mode) */}
      {!standalone && hasChanges && (
        <div className='flex items-center justify-between rounded-lg bg-slate-50 dark:bg-stone-900 border border-slate-200 dark:border-stone-800 p-3'>
          <div>
            <p className='text-sm font-medium text-slate-900 dark:text-white'>
              {isIncrease ? 'Increase' : 'Decrease'} to {pendingBlocks} blocks
            </p>
            <p className='text-xs text-slate-500 dark:text-stone-400'>
              {isIncrease ? 'Applied immediately, prorated.' : 'Takes effect at period end.'}
              {' '}
              <span className={priceDifference > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-green-600 dark:text-green-400'}>
                {priceDifference > 0 ? '+' : ''}{formatPrice(priceDifference)}/{intervalLabel}
              </span>
            </p>
          </div>
          <div className='flex gap-2'>
            <Button variant='outline' size='sm' onClick={handleCancel} disabled={loading}>
              Cancel
            </Button>
            <Button size='sm' onClick={handleApplyChanges} disabled={loading}>
              {loading ? 'Saving...' : 'Apply'}
            </Button>
          </div>
        </div>
      )}

      {/* Scheduled change (only in managed mode) */}
      {!standalone && subscription.scheduledStorageBlocks != null && subscription.scheduledChangeAt && (
        <div className='text-sm rounded-md bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 px-3 py-2'>
          <span className='text-blue-800 dark:text-blue-200'>
            Storage changing to{' '}
            <span className='font-medium'>
              {subscription.scheduledStorageBlocks} block{subscription.scheduledStorageBlocks !== 1 ? 's' : ''}
            </span>{' '}
            on {dayjs(subscription.scheduledChangeAt).format('MMM D, YYYY')}
          </span>
        </div>
      )}

      {/* Fine print (only in managed mode) */}
      {!standalone && (
        <ul className='text-[11px] text-slate-400 dark:text-stone-500 space-y-0.5'>
          <li>Increases apply immediately with prorated billing.</li>
          <li>Decreases take effect at period end.</li>
          <li>Volume discounts apply automatically.</li>
        </ul>
      )}
    </div>
  );

  if (standalone) {
    return content;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Storage</CardTitle>
        <CardDescription>
          Each block = 100 GB. {TIER_DISPLAY_NAMES[tier]} minimum: {minBlocks} block
          {minBlocks !== 1 ? 's' : ''}.
        </CardDescription>
      </CardHeader>
      <CardContent>{content}</CardContent>
    </Card>
  );
};
